/**
 * Dialog "Nuovo fornitore" riusabile — usato sia in /suppliers (con trigger
 * incluso) sia nel CertificationPanel del configuratore (controllato dall'esterno).
 *
 * Pattern: dialog controllato. Quando il fornitore viene creato chiama
 * `onCreated(supplier)` con `{ id, name }` per permettere al caller di
 * auto-selezionarlo.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useCreateOrgSupplier } from '@/hooks/useSupplierManagement';

interface CreateSupplierDialogProps {
  /** Stato open controllato. Se omesso, il dialog gestisce open internamente (richiede `showTrigger`). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Callback dopo creazione riuscita. Riceve `{ id, name }` del nuovo fornitore. */
  onCreated?: (supplier: { id: string; name: string }) => void;
  /** Pre-compila il campo Nome (es. ciò che l'utente stava digitando). */
  initialName?: string;
  /** Se true mostra il bottone trigger "Nuovo fornitore" integrato. Default: false. */
  showTrigger?: boolean;
  /** Label del bottone trigger se mostrato. Default "Nuovo fornitore". */
  triggerLabel?: string;
}

const CreateSupplierDialog: React.FC<CreateSupplierDialogProps> = ({
  open: openProp,
  onOpenChange,
  onCreated,
  initialName = '',
  showTrigger = false,
  triggerLabel = 'Nuovo fornitore',
}) => {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    else setOpenInternal(v);
  };

  const [name, setName] = useState(initialName);
  const [website, setWebsite] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [defaultDiscount, setDefaultDiscount] = useState('');
  const create = useCreateOrgSupplier();

  // Reset / sync initial name quando il dialog si apre
  useEffect(() => {
    if (open) {
      setName(initialName);
      setWebsite('');
      setContactEmail('');
      setDefaultDiscount('');
    }
  }, [open, initialName]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name, website, contact_email: contactEmail, default_discount: defaultDiscount },
      {
        onSuccess: (data) => {
          if (data) onCreated?.({ id: data.id, name: data.name });
          setOpen(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showTrigger && (
        <DialogTrigger asChild>
          <Button size="sm">
            <Plus className="mr-1 h-4 w-4" /> {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo fornitore</DialogTitle>
          <DialogDescription>
            Aggiungi un fornitore specifico per la tua organizzazione.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="cs-name">Nome*</Label>
            <Input
              id="cs-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Edilizia Rossi"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="cs-web">Sito web</Label>
            <Input
              id="cs-web"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div>
            <Label htmlFor="cs-email">Email contatto</Label>
            <Input
              id="cs-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cs-disc">Sconto default</Label>
            <Input
              id="cs-disc"
              value={defaultDiscount}
              onChange={(e) => setDefaultDiscount(e.target.value)}
              placeholder='es. "30+5"'
            />
            <p className="text-xs text-muted-foreground mt-1">
              Formato cumulativo, es. "30+5+3" = 30%, poi 5%, poi 3%.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creo…' : 'Crea'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSupplierDialog;
